import {
  Component,
  Input,
  Output,
  ElementRef,
  ViewChild,
  forwardRef,
  OnInit,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  EventEmitter
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

declare const EvaEditor: any;

@Component({
  selector: 'eva-editor',
  templateUrl: './eva-editor.component.html',
  styleUrls: ['./eva-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EvaEditorComponent),
      multi: true
    }
  ]
})
export class EvaEditorComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges, ControlValueAccessor {
  @Input() completions: any[] = [];
  @Input() role: 'text' | 'textarea' = 'textarea';
  @Input() tools: 'default' | 'extended' | 'tags-only' = 'default';


  @Input() inserts: any[] = [];
  @Input() showInserts: boolean = false;
  @Input() disallowTags: string[] = [];
  @Input() showSaveButton: boolean = false;

  private readonly unWantedTags: string[] = [
    'link',
    'accountid',
    'webevent.global.guest.bio.content',
    'webevent.global.presentation.content'
  ];
  @Output() save$ = new EventEmitter<void>();

  @ViewChild('editorContainer') container: ElementRef;

  private instance: any = null;
  private pendingValue: string = '';
  private propagateChange: (v: any) => void = () => {};
  private onTouched: () => void = () => {};
  private langChange$: any;

  constructor(private translateService: TranslateService) {}

  ngOnInit() {
    this.langChange$ = this.translateService.onLangChange.subscribe(() => {
      if (this.instance) {
        const currentContent = this.instance.getContent();
        this.instance.destroy();
        this.instance = null;
        this.mountEditor(currentContent);
      }
    });
  }

  ngAfterViewInit() {
    this.mountEditor(this.pendingValue);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['completions'] && !changes['completions'].firstChange && this.instance) {
      const currentContent = this.instance.getContent();
      this.instance.destroy();
      this.instance = null;
      this.mountEditor(currentContent);
    }
  }

  ngOnDestroy() {
    if (this.instance) {
      this.instance.destroy();
      this.instance = null;
    }
    if (this.langChange$) {
      this.langChange$.unsubscribe();
    }
  }

  private mountEditor(content: string) {
    const tags = this.mapTags(this.completions);
    this.instance = EvaEditor.createEvaEditor({
      element: this.container.nativeElement,
      content: content || '',
      tags: tags,
      role: this.role,
      tools: this.tools,
      theme: 'eva',
      lang: this.translateService.currentLang.slice(0, 2),
      onChange: (value: string) => {
        this.propagateChange(value);
      },
      onBlur: () => {
        this.onTouched();
      }
    });
  }

  private mapTags(completions: any[]): any[] {
    if (!completions) { return []; }
    const excluded = this.unWantedTags.concat(this.disallowTags);
    return completions
      .filter(t => excluded.indexOf(t.name) === -1)
      .map(t => ({
        tag: t.tag,
        label: t.label || t.name || t.tag,
        value: t.value,
        type: t.type
      }));
  }

  execInsert(insert: any) {
    if (!this.instance) { return; }
    this.instance.editor.chain().focus().insertContent(insert.args).run();
  }

  // ControlValueAccessor
  writeValue(value: any) {
    const str = value || '';
    if (this.instance) {
      this.instance.setContent(str);
    } else {
      this.pendingValue = str;
    }
  }

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/eva-editor/eva-editor.component.ts